/**
 * First-touch visitor attribution for acquisition reporting (admin Traffic page).
 *
 * Anonymous visitors can't call the API (every route is IAM-signed and the
 * identity pool has guest access off), so everything stays in localStorage
 * until sign-up and then travels exactly like a referral code:
 *   1. captureAttribution() runs once at startup: records the FIRST landing's
 *      utm_* tags, referrer host and landing path (never overwritten) and
 *      counts sessions before sign-up,
 *   2. recordDemoRound() (called by useGameEngine) counts demo rounds played,
 *   3. getAttributionMetadata() is sent as Cognito clientMetadata.attr on
 *      confirmSignUp, so PostConfirmation stores it on the new PROFILE
 *      atomically, and
 *   4. attemptAttributionClaim() is a best-effort fallback POST after sign-in.
 *      The server only accepts it for brand-new, unattributed accounts.
 */

import { apiPost } from '../hooks/apiClient.js';

const ATTR_KEY = 'atlas_attr';        // first touch — written once, never overwritten
const STATS_KEY = 'atlas_attr_stats'; // pre-sign-up engagement counters
const SENT_KEY = 'atlas_attr_sent';   // '1' once the server has made a decision
const SESSION_GAP_MS = 30 * 60 * 1000;
const MAX_TRACKED_GAMES = 60;

function clip(value, max) {
  return typeof value === 'string' ? value.trim().slice(0, max) : '';
}

function readJson(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode — attribution simply won't be recorded */
  }
}

// Storage errors read as "already sent" so nothing downstream keeps retrying.
function isSent() {
  try {
    return localStorage.getItem(SENT_KEY) === '1';
  } catch {
    return true;
  }
}

function markSent() {
  try {
    localStorage.setItem(SENT_KEY, '1');
    localStorage.removeItem(STATS_KEY);
  } catch {
    /* ignore */
  }
}

function newVisitorId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Record the first landing (once per browser) and count this page load as a
 * new session when the previous one was more than 30 minutes ago. Must run
 * before captureRefFromUrl(), which strips `?ref` from the address bar.
 */
export function captureAttribution() {
  if (typeof window === 'undefined') return;
  try {
    if (!readJson(ATTR_KEY)) {
      const params = new URLSearchParams(window.location.search);
      let referrer = '';
      try {
        if (document.referrer) {
          const r = new URL(document.referrer);
          if (r.host !== window.location.host) referrer = `${r.host}${r.pathname}`.slice(0, 200);
        }
      } catch {
        /* malformed referrer */
      }
      const source = clip(params.get('utm_source') || '', 64)
        || (params.get('ref') ? 'referral' : '')
        || referrer.split('/')[0]
        || 'direct';
      writeJson(ATTR_KEY, {
        source: source.toLowerCase(),
        medium: clip(params.get('utm_medium') || '', 64),
        campaign: clip(params.get('utm_campaign') || '', 64),
        content: clip(params.get('utm_content') || '', 64),
        term: clip(params.get('utm_term') || '', 100),
        referrer,
        landing: window.location.pathname.slice(0, 200),
        at: new Date().toISOString(),
        vid: newVisitorId(),
      });
    }

    if (isSent()) return;
    const stats = readJson(STATS_KEY) || { sessions: 0, demoRounds: 0, games: {} };
    const now = Date.now();
    if (!stats.lastSeenAt || now - stats.lastSeenAt > SESSION_GAP_MS) {
      stats.sessions = (stats.sessions || 0) + 1;
    }
    stats.lastSeenAt = now;
    writeJson(STATS_KEY, stats);
  } catch {
    /* storage blocked — the sign-up is reported as source "unknown" */
  }
}

/** Count one demo round (and which game) played before sign-up. */
export function recordDemoRound(gameId) {
  if (typeof window === 'undefined' || isSent()) return;
  const stats = readJson(STATS_KEY) || { sessions: 1, demoRounds: 0, games: {} };
  const games = stats.games || {};
  if (gameId && (games[gameId] || Object.keys(games).length < MAX_TRACKED_GAMES)) {
    games[gameId] = (games[gameId] || 0) + 1;
  }
  stats.games = games;
  stats.demoRounds = (stats.demoRounds || 0) + 1;
  stats.lastSeenAt = Date.now();
  writeJson(STATS_KEY, stats);
}

/**
 * The attribution payload as a compact JSON string (Cognito clientMetadata
 * values must be strings), or null when there's nothing to send.
 */
export function getAttributionMetadata() {
  if (typeof window === 'undefined' || isSent()) return null;
  const first = readJson(ATTR_KEY);
  if (!first) return null;
  const stats = readJson(STATS_KEY) || {};
  const payload = {
    ...first,
    sessions: stats.sessions || 0,
    demoRounds: stats.demoRounds || 0,
    demoGames: Object.keys(stats.games || {}).length,
  };
  for (const key of Object.keys(payload)) {
    if (payload[key] === '') delete payload[key];
  }
  return JSON.stringify(payload);
}

/**
 * Fallback attribution after sign-in. Safe on every sign-in: the server
 * ignores it for already-attributed or non-new accounts. Stops once the server
 * has decided (2xx or 4xx); a transient error keeps it for the next sign-in.
 */
export async function attemptAttributionClaim() {
  const attr = getAttributionMetadata();
  if (!attr) return;
  try {
    await apiPost('/user/attribution', { attr: JSON.parse(attr) });
    markSent();
  } catch (err) {
    if (err?.status && err.status >= 400 && err.status < 500) markSent();
  }
}
